/**
 * Returns the symbol for a given currency code.
 * Defaults to 'R$' for BRL and '$' for others if not found.
 */
export const getCurrencySymbol = (code?: string): string => {
    if (!code) return 'R$';
    
    const symbolMap: Record<string, string> = {
        'BRL': 'R$',
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'CIAL_TOKEN': 'token'
    };
    
    return symbolMap[code.toUpperCase()] || '$';
};

/**
 * Formats a number as currency based on the provided code.
 */
export const formatCurrency = (val: number, code: string = 'BRL', hideDecimals: boolean = false) => {
    if (code.toUpperCase() === 'CIAL_TOKEN') {
        const formattedValue = new Intl.NumberFormat('pt-BR', {
            maximumFractionDigits: hideDecimals ? 0 : 2
        }).format(val);
        const tokenLabel = Math.abs(val) === 1 ? 'token' : 'tokens';
        return `${formattedValue} ${tokenLabel}`;
    }

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: hideDecimals ? 0 : 2
    }).format(val);
};

/**
 * Like {@link formatCurrency}, but abbreviates large aggregates to "Mi"/"Bi"
 * (e.g. "R$ 1,5 Mi"). Only kicks in at >= 1M; below that it falls back to the
 * exact currency formatting. Currency-code aware (keeps the symbol and token
 * handling), so it's safe for non-BRL values — unlike the BRL-only variant in
 * utils/formatters. Use for big KPI/summary cards, never per-row table cells.
 */
export const formatLargeCurrency = (val: number, code: string = 'BRL', hideDecimals: boolean = false) => {
    const abs = Math.abs(val);
    if (abs >= 1_000_000) {
        const isBillion = abs >= 1_000_000_000;
        const compact = (val / (isBillion ? 1_000_000_000 : 1_000_000))
            .toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        const suffix = isBillion ? ' Bi' : ' Mi';

        if (code.toUpperCase() === 'CIAL_TOKEN') {
            return `${compact}${suffix} tokens`;
        }
        return `${getCurrencySymbol(code)} ${compact}${suffix}`;
    }

    return formatCurrency(val, code, hideDecimals);
};
